import * as XLSXstar from 'xlsx-js-style';
import {
  buildAllGrids, buildResumoGrid, type GridModel, type WorkbookGrids, type ColType,
  type ExcelFaturaItem, type ExcelErroItem,
} from './planilha/faturaGridModel';
import { carregarUcsGeradorasVigor } from './planilha/ucsGeradorasVigor';

// interop CJS/ESM: sob Vite `XLSXstar.utils` existe; sob Node puro fica em `.default`.
const XLSX: any = (XLSXstar as any).utils ? XLSXstar : (XLSXstar as any).default;

// Exportação Excel do módulo de extração v2.
// As MATRIZES (colunas nomeadas + linhas + tipo por coluna) vêm de `faturaGridModel.ts`
// — a mesma fonte que a planilha na tela consome, então tela e .xlsx nunca divergem.
// Aqui fica só o que é específico de Excel: estilo (paleta olive), numFmt e o writer.

export type { ExcelFaturaItem, ExcelErroItem } from './planilha/faturaGridModel';

const PAL = {
  header: '2E3D1F', sub: '5F6C37', headerFont: 'FFFFFF', zebra: 'EEF0E6',
  border: 'D9D9D9', ok: 'E2EFDA', revisar: 'FFF2CC', falhou: 'F8CBAD',
  yellow: 'FFFF00', red: 'F2DCDB',
};

const thin = { style: 'thin', color: { rgb: PAL.border } };
const borderAll = { top: thin, bottom: thin, left: thin, right: thin };

const numFmtFor = (t: ColType): string | undefined =>
  t === 'date' ? 'DD/MM/YYYY' : t === 'dec6' ? '#,##0.000000' : t === 'num' ? '#,##0.00' : undefined;

// ─────────────────────── aba FATURAS (larga, cabeçalho de 2 linhas) ───────────────────────
function faturasSheet(grid: GridModel) {
  const headers = grid.columns.map(c => c.label);
  const groupHeaders = grid.groupHeaders ?? new Array(headers.length).fill('');
  const ws = XLSX.utils.aoa_to_sheet([groupHeaders, headers, ...grid.rows]);

  const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
  for (let r = range.s.r; r <= range.e.r; r++) {
    for (let c = range.s.c; c <= range.e.c; c++) {
      const ref = XLSX.utils.encode_cell({ r, c });
      const cell = ws[ref];
      if (!cell) continue;
      const col = grid.columns[c];
      if (r >= 2) {
        if (col?.type === 'txt') { cell.t = 's'; cell.v = String(cell.v); }
        else if (typeof cell.v === 'number') { const z = numFmtFor(col?.type || 'num'); if (z) cell.z = z; }
      }
      const isHeader = r === 0 || r === 1;
      const s: any = {
        font: { name: 'Calibri', sz: isHeader ? 11 : 10, bold: isHeader, color: { rgb: r === 1 ? PAL.headerFont : '212121' } },
        alignment: { horizontal: c >= 2 ? 'center' : 'left', vertical: 'center', wrapText: isHeader },
        border: borderAll,
      };
      if (r === 1) s.fill = { fgColor: { rgb: PAL.header } };
      else if (r === 0) {
        if (col?.band === 'yellow') s.fill = { fgColor: { rgb: PAL.yellow } };
        else if (col?.band === 'red') s.fill = { fgColor: { rgb: PAL.red } };
      } else if (c === grid.highlightCol && String(cell.v ?? '') !== '') {
        // Motivo Revisão: fundo âmbar + texto escuro em negrito (pedido: destaque no início).
        s.fill = { fgColor: { rgb: PAL.revisar } };
        s.font = { ...s.font, bold: true, color: { rgb: '92400E' } };
        s.alignment = { ...s.alignment, horizontal: 'left', wrapText: true };
      } else if (r % 2 === 1) s.fill = { fgColor: { rgb: PAL.zebra } };
      cell.s = s;
    }
  }

  ws['!cols'] = headers.map((h, i) => {
    let maxLen = String(h).length;
    const gh = groupHeaders[i] ? String(groupHeaders[i]) : '';
    if (gh.length > maxLen) maxLen = Math.min(gh.length, 30);
    for (const row of grid.rows) { const v = row[i]; const l = v == null ? 0 : String(v).length; if (l > maxLen) maxLen = l; }
    return { wpx: Math.min(Math.max(Math.ceil(maxLen * 7.2) + 18, 80), 380) };
  });
  (ws as any)['!freeze'] = { xSplit: 0, ySplit: 2, topLeftCell: 'A3', activePane: 'bottomLeft', state: 'frozen' };
  return ws;
}

// sheet simples (abas Itens/Medidor/Auditoria/Erros) — header olive + zebra
function simpleSheet(grid: GridModel) {
  const headers = grid.columns.map(c => c.label);
  const ws = XLSX.utils.aoa_to_sheet([headers, ...grid.rows]);
  const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
  for (let r = range.s.r; r <= range.e.r; r++) {
    for (let c = range.s.c; c <= range.e.c; c++) {
      const ref = XLSX.utils.encode_cell({ r, c });
      const cell = ws[ref];
      if (!cell) continue;
      if (r === 0) {
        cell.s = { font: { name: 'Calibri', bold: true, sz: 11, color: { rgb: PAL.headerFont } }, fill: { fgColor: { rgb: PAL.header } }, alignment: { horizontal: 'center', vertical: 'center', wrapText: true }, border: borderAll };
      } else {
        const type = grid.columns[c]?.type || 'txt';
        const numeric = type !== 'txt';
        const horizontal = type === 'date' ? 'center' : numeric ? 'right' : 'left';
        cell.s = { font: { name: 'Calibri', sz: 10, color: { rgb: '212121' } }, fill: { fgColor: { rgb: r % 2 === 0 ? PAL.zebra : 'FFFFFF' } }, alignment: { horizontal, vertical: 'center' }, border: borderAll };
        if (numeric && typeof cell.v === 'number') { const z = numFmtFor(type); if (z) cell.z = z; }
      }
    }
  }
  // Auditoria: colore a coluna "Tipo" (severityCol) por severidade.
  if (grid.severityCol !== undefined) {
    for (let r = 1; r <= range.e.r; r++) {
      const cell = ws[XLSX.utils.encode_cell({ r, c: grid.severityCol })];
      if (!cell) continue;
      const v = String(cell.v);
      const bg = v === 'critico' ? PAL.falhou : v === 'alerta' ? PAL.revisar : PAL.ok;
      cell.s = { ...cell.s, fill: { fgColor: { rgb: bg } }, font: { ...(cell.s?.font || {}), bold: true } };
    }
  }
  ws['!cols'] = headers.map((h, i) => {
    let max = String(h).length;
    for (const r of grid.rows) { const v = r[i]; const l = v == null ? 0 : String(v).length; if (l > max) max = l; }
    return { wpx: Math.min(Math.max(Math.ceil(max * 7.2) + 16, 70), 360) };
  });
  (ws as any)['!freeze'] = { xSplit: 0, ySplit: 1, topLeftCell: 'A2', activePane: 'bottomLeft', state: 'frozen' };
  return ws;
}

const fileStamp = () => {
  const now = new Date();
  return `${now.toLocaleDateString('pt-BR').replace(/\//g, '-')}_${now.toLocaleTimeString('pt-BR').replace(/:/g, '')}`;
};

/** Escreve o workbook a partir das MATRIZES já montadas (possivelmente EDITADAS na tela).
 *  `ucsVigor` (UCs das usinas da Vigor) faz o Resumo separar a injeção por origem — sem
 *  elas a aba sai com a coluna única de total, igual à tela. */
export function writeWorkbook(grids: WorkbookGrids, filename?: string, ucsVigor?: Set<string> | null): void {
  const wb = XLSX.utils.book_new();
  // Resumo primeiro: é o recorte que se lê sem rolar as ~160 colunas da aba Faturas.
  // Derivado dela (inclusive das correções manuais) — nunca de uma segunda leitura.
  const resumo = buildResumoGrid(grids.faturas, ucsVigor);
  if (resumo) XLSX.utils.book_append_sheet(wb, simpleSheet(resumo), 'Resumo');
  if (grids.faturas && grids.faturas.rows.length) XLSX.utils.book_append_sheet(wb, faturasSheet(grids.faturas), 'Faturas');
  if (grids.itens && grids.itens.rows.length) XLSX.utils.book_append_sheet(wb, simpleSheet(grids.itens), 'Itens');
  if (grids.medidor && grids.medidor.rows.length) XLSX.utils.book_append_sheet(wb, simpleSheet(grids.medidor), 'Medidor');
  if (grids.auditoria && grids.auditoria.rows.length) XLSX.utils.book_append_sheet(wb, simpleSheet(grids.auditoria), 'Auditoria');
  if (grids.erros && grids.erros.rows.length) XLSX.utils.book_append_sheet(wb, simpleSheet(grids.erros), 'Erros');
  XLSX.writeFile(wb, filename || `Extracao_Faturas_${fileStamp()}.xlsx`);
}

/** Caminho clássico: monta as matrizes dos dados e baixa o Excel. Espera as UCs das
 *  usinas (cache de módulo, uma consulta por sessão) para o Resumo sair igual ao da tela;
 *  se elas não vierem, a aba mantém a coluna única de injeção. */
export async function exportExtracoesToExcel(faturas: ExcelFaturaItem[], erros: ExcelErroItem[] = []): Promise<void> {
  writeWorkbook(buildAllGrids(faturas, erros), undefined, await carregarUcsGeradorasVigor());
}
